import React from 'react'
import { TrashIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { BOT_PERMISSION_GROUPS } from '../bot-tokens/bot-permission-groups'
import { cn } from 'src/utils/style'

export interface GrantedPermissionGroup {
  module: string
  label: string
  items: { codename: string; label: string }[]
}

/**
 * Permissions grouped by module (in picker order), for display as chips.
 * Codenames outside the picker's groups land in a final group labelled ``otherLabel``.
 */
export function groupedPermissions(
  permissions: string[],
  otherLabel: string,
): GrantedPermissionGroup[] {
  const granted = new Set(permissions)
  const known = new Set<string>()
  const groups: GrantedPermissionGroup[] = []
  for (const group of BOT_PERMISSION_GROUPS) {
    const defs = [...(group.access ? [group.access] : []), ...group.permissions]
    const items = defs.filter((d) => granted.has(d.codename))
    defs.forEach((d) => known.add(d.codename))
    if (items.length) groups.push({ module: group.module, label: group.label, items })
  }
  const other = permissions.filter((p) => !known.has(p))
  if (other.length) {
    groups.push({
      module: 'other',
      label: otherLabel,
      items: other.map((codename) => ({ codename, label: codename })),
    })
  }
  return groups
}

interface PermissionChipsProps {
  permissions: string[]
  otherLabel: string
  emptyText: string
  /** Prefix for each group row's testid: `${groupTestIdPrefix}-${module}`. */
  groupTestIdPrefix: string
  chipTestId?: (codename: string) => string
  /** When given, permissions outside this set render muted and struck through. */
  effective?: Set<string>
  /** Title for an ineffective chip, given the chip's group + action label. */
  ineffectiveTitle?: (label: string) => string
}

/** A token's permissions as chips grouped by module. */
export const PermissionChips: React.FC<PermissionChipsProps> = ({
  permissions,
  otherLabel,
  emptyText,
  groupTestIdPrefix,
  chipTestId,
  effective,
  ineffectiveTitle,
}) => {
  const groups = groupedPermissions(permissions, otherLabel)
  if (!groups.length) return <p className="text-sm text-text-placeholder">{emptyText}</p>
  return (
    <dl className="space-y-1.5">
      {groups.map((group) => (
        <div
          key={group.module}
          className="flex flex-wrap items-center gap-1.5"
          data-testid={`${groupTestIdPrefix}-${group.module}`}
        >
          <dt className="mr-1 text-sm text-text-primary">{group.label}</dt>
          {group.items.map((item) => {
            const isEffective = !effective || effective.has(item.codename)
            const fullLabel =
              group.module === 'other' ? item.label : `${group.label}: ${item.label}`
            return (
              <dd
                key={item.codename}
                className={cn([
                  'rounded-full px-2 py-0.5 text-xs',
                  isEffective
                    ? 'bg-surface-hover text-text-secondary'
                    : 'bg-surface text-text-tertiary line-through ring-1 ring-inset ring-border',
                ])}
                title={!isEffective && ineffectiveTitle ? ineffectiveTitle(fullLabel) : fullLabel}
                data-testid={chipTestId?.(item.codename)}
                data-effective={effective ? isEffective : undefined}
              >
                {item.label}
              </dd>
            )
          })}
        </div>
      ))}
    </dl>
  )
}

export const TokenCardGrid: React.FC<{ testId: string; children: React.ReactNode }> = ({
  testId,
  children,
}) => (
  <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid={testId}>
    {children}
  </ul>
)

interface TokenCardProps {
  testId: string
  name: string
  prefix: string
  /** Extra markers under the prefix (e.g. a connection badge). */
  badge?: React.ReactNode
  permissionsTitle: string
  permissionsTestId?: string
  permissions: React.ReactNode
  /** Footer facts, three per card (e.g. last used, created by, created). */
  meta: { label: string; value: React.ReactNode }[]
  revokeAriaLabel: string
  revokeTitle: string
  revokeTestId: string
  onRevoke: () => void
}

/** One API token: name + prefix + revoke, its permissions, and a footer of dates. */
export const TokenCard: React.FC<TokenCardProps> = ({
  testId,
  name,
  prefix,
  badge,
  permissionsTitle,
  permissionsTestId,
  permissions,
  meta,
  revokeAriaLabel,
  revokeTitle,
  revokeTestId,
  onRevoke,
}) => (
  <li
    className="flex min-w-0 flex-col gap-3 rounded-xl border border-border bg-surface p-4"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate font-medium text-text-primary" title={name}>
          {name}
        </p>
        <p className="mt-0.5 font-mono text-xs text-text-secondary">{prefix}</p>
        {badge}
      </div>
      <Button
        variant="danger"
        size="sm"
        iconOnly
        shape="square"
        aria-label={revokeAriaLabel}
        title={revokeTitle}
        onClick={onRevoke}
        data-testid={revokeTestId}
      >
        <TrashIcon className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>

    <div data-testid={permissionsTestId}>
      <p className="mb-1.5 text-xs font-medium uppercase text-text-secondary">{permissionsTitle}</p>
      {permissions}
    </div>

    <dl className="mt-auto grid grid-cols-3 gap-2 border-t border-border pt-3 text-xs text-text-secondary">
      {meta.map((m) => (
        <div key={m.label} className="min-w-0">
          <dt>{m.label}</dt>
          <dd className="truncate text-text-primary">{m.value}</dd>
        </div>
      ))}
    </dl>
  </li>
)
